import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Refresh,
  CheckCircle,
  Error,
  Schedule,
  Cancel,
  PlayArrow,
  Warning,
} from "@mui/icons-material";
import { useOdooMigrationPermissions } from "../../hooks/useOdooMigrationPermissions";
import { odooProductionMigrationService } from "../../services/odooProductionMigrationService";
import type { ProductionMigrationLog } from "../../types/odoo-migration";

interface OdooProductionMigrationLogsProps {
  defaultLimit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export function OdooProductionMigrationLogs({
  defaultLimit = 10,
  autoRefresh = false,
  refreshInterval = 30000,
}: OdooProductionMigrationLogsProps) {
  const { canViewLogs, isLoading: permissionsLoading } =
    useOdooMigrationPermissions();
  const [logs, setLogs] = useState<ProductionMigrationLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(defaultLimit);

  const fetchLogs = async (requestedLimit?: number) => {
    try {
      setError(null);
      setIsLoading(true);
      const currentLimit = requestedLimit || limit;
      const migrationLogs =
        await odooProductionMigrationService.getMigrationLogsWithLimit(
          currentLimit,
        );
      setLogs(migrationLogs);
    } catch (err: any) {
      const errorMessage =
        (err as Error)?.message || String(err) || "Error al cargar logs";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value) || defaultLimit;
    setLimit(newLimit);
    fetchLogs(newLimit);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle color="success" fontSize="small" />;
      case "completed_with_warnings":
        return <Warning color="warning" fontSize="small" />;
      case "failed":
        return <Error color="error" fontSize="small" />;
      case "cancelled":
        return <Cancel color="warning" fontSize="small" />;
      case "started":
        return <PlayArrow color="primary" fontSize="small" />;
      default:
        return <Schedule color="action" fontSize="small" />;
    }
  };

  const getStatusColor = (
    status: string,
  ): "success" | "error" | "warning" | "primary" | "default" => {
    switch (status) {
      case "completed":
        return "success";
      case "completed_with_warnings":
        return "warning";
      case "failed":
        return "error";
      case "cancelled":
        return "warning";
      case "started":
        return "primary";
      default:
        return "default";
    }
  };

  const formatDuration = (durationSeconds?: number) => {
    if (!durationSeconds) return "-";

    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.floor((durationSeconds % 3600) / 60);
    const seconds = Math.floor(durationSeconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "-";

    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  useEffect(() => {
    if (!permissionsLoading && canViewLogs) {
      fetchLogs();
    }
  }, [permissionsLoading, canViewLogs]);

  useEffect(() => {
    if (autoRefresh && canViewLogs) {
      const interval = setInterval(() => {
        fetchLogs();
      }, refreshInterval);

      return () => clearInterval(interval);
    }

    return undefined;
  }, [autoRefresh, refreshInterval, canViewLogs]);

  if (permissionsLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Verificando permisos...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!canViewLogs) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            No tiene permisos para ver el historial de sincronizaciones
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Historial de Sincronizaciones"
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              label="Límite"
              type="number"
              value={limit}
              onChange={handleLimitChange}
              size="small"
              sx={{ width: 100 }}
              inputProps={{ min: 1, max: 100 }}
            />
            <Tooltip title="Actualizar historial">
              <span>
                <IconButton onClick={() => fetchLogs()} disabled={isLoading}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />

      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Cargando logs...</Typography>
          </Box>
        ) : logs.length === 0 ? (
          <Alert severity="info">
            No hay registros de sincronizacion disponibles
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Estado</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Duración</TableCell>
                  <TableCell>Progreso</TableCell>
                  <TableCell>Tamaño DB</TableCell>
                  <TableCell>Razón</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {getStatusIcon(log.status)}
                          <Chip
                            label={log.status.toUpperCase()}
                            color={getStatusColor(log.status)}
                            size="small"
                          />
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            log.executionType === "manual" ? "Manual" : "Auto"
                          }
                          color={
                            log.executionType === "manual" ? "warning" : "default"
                          }
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {log.userEmail || log.userId}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.startTime).toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatDuration(log.durationSeconds)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{log.progress}%</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatFileSize(log.databaseSizeBytes)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={log.triggerReason || "-"}
                        >
                          {log.triggerReason || "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {log.postProcessWarnings?.length ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ pt: 0 }}>
                          {log.postProcessWarnings.map((w, i) => (
                            <Typography
                              key={i}
                              variant="caption"
                              component="div"
                              sx={{ color: "warning.main" }}
                            >
                              {w}
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
